import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, BookOpen, Feather, ChevronRight, RefreshCw, Zap, X } from "lucide-react";
import { useAuth } from "@/auth";
import { apiFetch, BACKEND_ORIGIN } from "@/api";
import { Spinner, cn } from "@/ui";
import { BottomNav } from "@/components/BottomNav";

const MODE_META = {
  everyday: { label: "ask", cls: "text-ask" },
  learn: { label: "learn", cls: "text-learn" },
  create: { label: "create", cls: "text-create" },
  research: { label: "research", cls: "text-research" },
};

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Home() {
  const navigate = useNavigate();
  const { account, booting } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [xp, setXp] = useState(null);
  const [xpHelp, setXpHelp] = useState(false);

  useEffect(() => {
    if (!booting && !account) navigate("/onboarding");
  }, [booting, account]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const d = await apiFetch("/user/home-suggestions");
      setSuggestions((d.suggestions || []).slice(0, 3));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => { try { const p = await apiFetch("/user/points"); setXp(p); } catch {} })();
  }, []);

  const openMode = (tab, q) => {
    navigate(`/chat?tab=${tab}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
  };

  const first = account?.name ? account.name.split(" ")[0] : "";
  const initial = (account?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <main className="max-scroll flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-2xl px-5 py-8 sm:px-6">
          {/* Greeting + avatar */}
          <div className="flex items-center justify-between">
            <p className="text-base text-secondary-foreground">{greeting()}{first ? `, ${first}` : ""}</p>
            <button
              data-testid="home-profile"
              onClick={() => navigate("/settings")}
              className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-border bg-input-bg font-display text-base font-bold"
            >
              {account?.avatar_url ? (
                <img src={BACKEND_ORIGIN + account.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                initial
              )}
            </button>
          </div>

          <h1 className="sr-only">MAXx Home</h1>

          {/* XP + streak */}
          {xp ? (
            <div
              data-testid="home-xp"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/chat?tab=learn")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/chat?tab=learn"); } }}
              className="mt-5 flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border border-border bg-secondary px-5 py-4 text-left transition-transform duration-200 active:scale-[0.99]"
            >
              <span data-testid="home-xp-total" className="flex items-center gap-2 font-display text-lg font-bold"><Zap className="h-5 w-5 text-learn" /> {xp.total_xp} XP</span>
              <div className="flex items-center gap-2">
                {xp.streak_days ? <span data-testid="home-xp-streak" className="text-sm font-semibold text-muted-foreground">🔥 {xp.streak_days} day streak</span> : null}
                <button
                  type="button"
                  data-testid="home-xp-help"
                  aria-label="How XP works"
                  onClick={(e) => { e.stopPropagation(); setXpHelp(true); }}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-background text-xs font-bold text-muted-foreground transition-colors hover:text-foreground"
                >?</button>
              </div>
            </div>
          ) : null}

          {/* Ask hero */}
          <button
            data-testid="home-ask"
            onClick={() => openMode("everyday")}
            className="group mt-6 flex w-full items-center gap-4 rounded-3xl bg-primary px-6 py-6 text-left text-primary-foreground transition-transform duration-200 active:scale-[0.98]"
          >
            <div className="flex-1">
              <div className="font-display text-2xl font-bold sm:text-3xl">Ask anything</div>
              <div className="mt-0.5 text-base opacity-90">Type or talk</div>
            </div>
            <div className="h-10 w-px bg-primary-foreground/35" />
            <Mic className="h-8 w-8" strokeWidth={2} />
          </button>

          {/* Explore */}
          <h2 className="mt-8 font-display text-xl font-bold">Explore</h2>
          <div className="mt-3 grid grid-cols-2 gap-4">
            <ExploreCard testid="home-learn" label="Learn" Icon={BookOpen} accentClass="text-learn" onClick={() => openMode("learn")} />
            <ExploreCard testid="home-create" label="Create" Icon={Feather} accentClass="text-create" onClick={() => openMode("create")} />
          </div>

          {/* Try something */}
          <h2 className="mt-8 font-display text-xl font-bold">Try something</h2>
          <div className="mt-3 flex flex-col gap-2.5">
            {loading ? (
              [0, 1, 2].map((i) => <div key={i} className="h-[54px] animate-pulse rounded-2xl bg-secondary" />)
            ) : error ? (
              <button
                data-testid="home-suggestions-retry"
                onClick={load}
                className="flex items-center justify-between rounded-2xl border border-border bg-secondary px-4 py-4 text-left"
              >
                <span className="text-secondary-foreground">Couldn't load ideas. Tap to retry.</span>
                <RefreshCw className="h-4 w-4 text-muted-foreground" />
              </button>
            ) : (
              suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`home-suggestion-${i}`}
                  onClick={() => openMode(s.tab, s.text)}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-secondary px-4 py-3 text-left transition-colors hover:bg-accent/40"
                >
                  <div className="min-w-0">
                    <div className={cn("text-[11px] font-semibold uppercase tracking-wider", (MODE_META[s.tab] || MODE_META.everyday).cls)}>
                      {(MODE_META[s.tab] || MODE_META.everyday).label}
                    </div>
                    <div className="font-medium">{s.text}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))
            )}
          </div>
        </div>
      </main>
      {xpHelp ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setXpHelp(false)}>
          <div data-testid="xp-help-modal" className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Zap className="h-5 w-5 text-learn" /> How XP works</h2>
              <button data-testid="xp-help-close" onClick={() => setXpHelp(false)}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground">The more you use MAXx, the more XP you gain.</p>
            <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold text-foreground">🔥 Streak</span> — how many days in a row you've used MAXx.</p>
          </div>
        </div>
      ) : null}
      <BottomNav active="home" />
    </div>
  );
}

function ExploreCard({ testid, label, Icon, accentClass, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "flex min-h-[140px] flex-col justify-end rounded-3xl border border-border bg-secondary p-5 text-left transition-transform duration-200 active:scale-[0.98] hover:border-primary/40",
      )}
    >
      <Icon className={cn("h-8 w-8", accentClass)} strokeWidth={2} />
      <span className="mt-3 font-display text-xl font-bold">{label}</span>
    </button>
  );
}
